import { Moon, Sun, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { FadeIn } from '../components/AnimatedPanel';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export function SettingsPanel() {
  const { user, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [bio, setBio] = useState(user?.bio ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setBio(user?.bio ?? '');
  }, [user?.bio]);

  if (!user) {
    return (
      <div className="panel-card text-center">
        <p className="text-korvex-muted">Sign in to manage your settings.</p>
      </div>
    );
  }

  async function saveBio() {
    setSaving(true);
    setMessage('');
    try {
      const { user: updated } = await api.updateSettings({ bio });
      updateUser(updated);
      setMessage('Profile updated.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <FadeIn>
        <h2 className="font-display text-3xl font-bold">Settings</h2>
        <p className="text-korvex-muted">Customize your Korvex experience.</p>
      </FadeIn>

      <FadeIn delay={0.05}>
        <div className="panel-card space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-korvex-accent/15">
              {theme === 'dark' ? <Moon className="h-5 w-5 text-korvex-accent" /> : <Sun className="h-5 w-5 text-amber-500" />}
            </div>
            <div>
              <h3 className="font-semibold">Appearance</h3>
              <p className="text-sm text-korvex-muted">Toggle dark mode across the platform.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className={`btn-ghost flex-1 ${theme === 'dark' ? 'ring-2 ring-korvex-accent/50' : ''}`}
              onClick={() => setTheme('dark')}
            >
              <Moon className="h-4 w-4" />
              Dark
            </button>
            <button
              type="button"
              className={`btn-ghost flex-1 ${theme === 'light' ? 'ring-2 ring-korvex-violet/50' : ''}`}
              onClick={() => setTheme('light')}
            >
              <Sun className="h-4 w-4" />
              Light
            </button>
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <div className="panel-card space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-korvex-violet/15">
              <User className="h-5 w-5 text-korvex-violet" />
            </div>
            <div>
              <h3 className="font-semibold">Profile bio</h3>
              <p className="text-sm text-korvex-muted">Tell others about yourself.</p>
            </div>
          </div>
          <textarea
            className="input-field min-h-[100px] resize-y"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            placeholder="Creator, explorer, game builder…"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-korvex-muted">{bio.length}/500</span>
            <button type="button" className="btn-primary" onClick={() => void saveBio()} disabled={saving}>
              {saving ? 'Saving…' : 'Save bio'}
            </button>
          </div>
          {message && <p className="text-sm text-korvex-accent">{message}</p>}
        </div>
      </FadeIn>

      <FadeIn delay={0.15}>
        <div className="panel-card">
          <h3 className="font-semibold">Account</h3>
          <p className="mt-1 text-sm text-korvex-muted">Username: {user.username}</p>
          <p className="text-sm text-korvex-muted">
            Member since {new Date(user.createdAt).toLocaleDateString()}
          </p>
        </div>
      </FadeIn>
    </div>
  );
}
